import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FilterOption, FilterSizeVariants } from "../types";

const inferModeFromSelectedSizes = (
  selectedKeys: string[],
  simpleGroups: any[],
  advancedGroups: any[],
  flattenLeafOptions: (node: any) => FilterOption[]
): FilterSizeVariants => {
  if (!selectedKeys.length) return FilterSizeVariants.SIMPLE;

  const advancedOptionKeys = new Set<string>();
  advancedGroups.forEach(group => {
    flattenLeafOptions(group).forEach(option => {
      if (typeof option?.key === "string") advancedOptionKeys.add(option.key);
    });
  });

  const simpleOptionKeys = new Set<string>();
  simpleGroups.forEach(group => {
    flattenLeafOptions(group).forEach(option => {
      if (typeof option?.key === "string") simpleOptionKeys.add(option.key);
    });
  });

  const hasAdvancedSelection = selectedKeys.some(key =>
    advancedOptionKeys.has(key)
  );
  const hasSimpleSelection = selectedKeys.some(key =>
    simpleOptionKeys.has(key)
  );

  if (hasAdvancedSelection) return FilterSizeVariants.ADVANCED;
  if (hasSimpleSelection) return FilterSizeVariants.SIMPLE;
  return FilterSizeVariants.SIMPLE;
};

export const useSizeFilterMode = ({
  facetId,
  queryParam,
  selectedFilters,
  simpleGroups,
  advancedGroups,
  flattenLeafOptions,
  filterOptionsToCurrentFacet,
  modeSwitchClearedSelectionsRef
}: {
  facetId: string;
  queryParam: string;
  selectedFilters: Record<string, any>;
  simpleGroups: any[];
  advancedGroups: any[];
  flattenLeafOptions: (node: any) => FilterOption[];
  filterOptionsToCurrentFacet: (
    opts: FilterOption[] | undefined | null
  ) => FilterOption[];
  modeSwitchClearedSelectionsRef: React.MutableRefObject<boolean>;
}) => {
  const [userChosenModeByFacetId, setUserChosenModeByFacetId] = useState<
    Record<string, FilterSizeVariants>
  >({});
  const [modeChangeOriginByFacetId, setModeChangeOriginByFacetId] = useState<
    Record<string, "user" | "auto">
  >({});

  const facetHasSelectedSizes = useMemo(() => {
    const rawOptions = selectedFilters?.[queryParam]?.selectedOptions;
    return filterOptionsToCurrentFacet(rawOptions).some(
      option => typeof option?.key === "string" && option.key.length > 0
    );
  }, [selectedFilters, queryParam, filterOptionsToCurrentFacet]);

  const modeInferredFromSelections = useMemo(() => {
    const rawOptions = selectedFilters?.[queryParam]?.selectedOptions;
    const scopedOptions = filterOptionsToCurrentFacet(rawOptions);
    const selectedKeys = scopedOptions
      .map((option: FilterOption) => option?.key)
      .filter((key): key is string => typeof key === "string");

    return inferModeFromSelectedSizes(
      selectedKeys,
      simpleGroups,
      advancedGroups,
      flattenLeafOptions
    );
  }, [
    selectedFilters,
    queryParam,
    filterOptionsToCurrentFacet,
    simpleGroups,
    advancedGroups,
    flattenLeafOptions
  ]);

  const previousFacetIdRef = useRef(facetId);
  const previousFacetHadSelectionsRef = useRef(facetHasSelectedSizes);

  useEffect(() => {
    previousFacetHadSelectionsRef.current = facetHasSelectedSizes;
  }, [facetHasSelectedSizes]);

  useEffect(() => {
    const leavingFacetId = previousFacetIdRef.current;
    previousFacetIdRef.current = facetId;
    if (leavingFacetId === facetId) return;

    if (modeSwitchClearedSelectionsRef.current) {
      modeSwitchClearedSelectionsRef.current = false;
    }

    if (!previousFacetHadSelectionsRef.current && leavingFacetId) {
      setUserChosenModeByFacetId(prev => {
        if (!(leavingFacetId in prev)) return prev;
        const next = { ...prev };
        delete next[leavingFacetId];
        return next;
      });
      setModeChangeOriginByFacetId(prev => {
        if (!(leavingFacetId in prev)) return prev;
        const next = { ...prev };
        delete next[leavingFacetId];
        return next;
      });
    }
  }, [facetId, modeSwitchClearedSelectionsRef]);

  const activeMode =
    userChosenModeByFacetId[facetId] ??
    (!facetHasSelectedSizes
      ? FilterSizeVariants.SIMPLE
      : modeInferredFromSelections);

  const activeModeOrigin: "user" | "auto" =
    userChosenModeByFacetId[facetId] !== undefined
      ? modeChangeOriginByFacetId[facetId] ?? "auto"
      : !facetHasSelectedSizes
      ? "auto"
      : modeChangeOriginByFacetId[facetId] ?? "auto";

  const setMode = useCallback(
    (nextMode: FilterSizeVariants, reason: "user" | "auto") => {
      setModeChangeOriginByFacetId(prev => ({
        ...prev,
        [facetId]: reason
      }));
      if (reason === "user") {
        setUserChosenModeByFacetId(prev => ({
          ...prev,
          [facetId]: nextMode
        }));
      }
    },
    [facetId]
  );

  return {
    activeMode,
    activeModeOrigin,
    setMode,
    userChosenModeByFacetId,
    setUserChosenModeByFacetId,
    facetHasSelectedSizes
  };
};
